import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import * as Sentry from "@sentry/react";
import { X, Search, ImagePlus, Trash2, CircleCheck, TriangleAlert } from "lucide-react";
import { POINT_TYPE_LIST, getPointType, formatFullAddress } from "./mapPointTypes";
import InteractiveMap from "./InteractiveMap";
import {
  createMapPoint,
  updateMapPoint,
  uploadMapPointImage,
  deleteMapPointImage,
  geocodeAddress,
} from "services/mapPointService";

const MAX_SIZE = 5 * 1024 * 1024; // 5 MB
const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp"];

const inputClass =
  "w-full px-3 py-2 rounded bg-slate-800 border border-slate-700 text-slate-50 focus:outline-none focus:ring focus:border-amber-400";
const labelClass = "block mb-1 text-sm font-medium text-slate-200";

// "123A rue X" → "123A": used when OSM knows the street but not the civic number.
const typedHouseNumber = (text) => text.trim().match(/^(\d+[A-Za-z]?)\b/)?.[1] || "";

/**
 * Create / edit modal for a map point. `point` is null when creating.
 * `locations` is the full locations list; only active ones can be linked to a weekly point.
 * `allowedTypes` limits the selectable types (admins may only create simple places).
 * The address must be localized (geocoded) before saving, so the stored address is complete.
 */
const MapPointForm = ({
  point,
  locations = [],
  allowedTypes = POINT_TYPE_LIST.map((t) => t.key),
  currentUserId,
  onClose,
  onSaved,
}) => {
  const isEdit = !!point;
  const [type, setType] = useState(
    point?.type && allowedTypes.includes(point.type) ? point.type : allowedTypes[0]
  );
  const [name, setName] = useState(point?.name || "");
  const [locationId, setLocationId] = useState(point?.locationId || "");
  const [address, setAddress] = useState(point?.address || "");
  const [town, setTown] = useState(point?.town || "");
  const [province, setProvince] = useState(point?.province || "");
  const [postalCode, setPostalCode] = useState(point?.postalCode || "");
  // The full, geocoded address. Cleared whenever the street or town is edited by hand.
  const [verifiedAddress, setVerifiedAddress] = useState(point?.fullAddress || "");
  const [description, setDescription] = useState(point?.description || "");
  const [website, setWebsite] = useState(point?.website || "");
  const [position, setPosition] = useState(
    Number.isFinite(point?.lat) && Number.isFinite(point?.lng)
      ? { lat: point.lat, lng: point.lng }
      : null
  );
  const [flyTo, setFlyTo] = useState(null);
  const [geoResults, setGeoResults] = useState([]);
  const [geoLoading, setGeoLoading] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(point?.image || null);
  const [removeImage, setRemoveImage] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const fileInputRef = useRef(null);
  const objectUrlRef = useRef(null);

  const activeLocations = useMemo(
    () =>
      locations
        .filter((l) => l.active === true)
        .sort((a, b) =>
          `${a.name} ${a.table || ""}`.localeCompare(`${b.name} ${b.table || ""}`, "fr")
        ),
    [locations]
  );
  const linkedLocation = locations.find((l) => l.id === locationId) || null;
  const linkedImage = type === "weekly" && linkedLocation?.image?.trim() ? linkedLocation.image : null;

  // Close on Escape, lock the page scroll while open, clean up preview URLs.
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    };
  }, [onClose]);

  const handleLocationChange = (id) => {
    setLocationId(id);
    const loc = locations.find((l) => l.id === id);
    if (!loc) return;
    if (!name.trim()) setName(loc.name || "");
    if (!town.trim() && loc.town) {
      setTown(loc.town);
      setVerifiedAddress("");
    }
  };

  const editAddressPart = (setter) => (e) => {
    setter(e.target.value);
    setVerifiedAddress("");
    setGeoResults([]);
  };

  const setPositionAndFly = (lat, lng, zoom = 16) => {
    setPosition({ lat, lng });
    setFlyTo({ lat, lng, zoom });
  };

  const handleGeocode = async () => {
    const query = [address, town].filter((s) => s && s.trim()).join(", ");
    if (!address.trim() || !town.trim()) {
      setError("Entrez le numéro civique, la rue et la ville avant de localiser l'adresse.");
      return;
    }
    setError("");
    setGeoLoading(true);
    setGeoResults([]);
    try {
      const typed = typedHouseNumber(address);
      const results = (await geocodeAddress(query)).map((r) => {
        const houseNumber = r.houseNumber || typed;
        return { ...r, houseNumber, complete: !!(houseNumber && r.road && r.town) };
      });
      const complete = results.filter((r) => r.complete);
      if (results.length === 0) {
        setError("Aucune adresse trouvée. Vérifiez le numéro civique, la rue et la ville.");
      } else if (complete.length === 0) {
        setError(
          "Aucune adresse complète trouvée (numéro civique, rue et ville). Précisez l'adresse et réessayez."
        );
      } else if (results.length === 1) {
        applyGeoResult(results[0]);
      } else {
        setGeoResults(results);
      }
    } catch (err) {
      Sentry.captureException(err);
      console.error("Geocoding error:", err);
      setError("La recherche d'adresse a échoué. Réessayez dans quelques secondes.");
    } finally {
      setGeoLoading(false);
    }
  };

  const applyGeoResult = (result) => {
    if (!result.complete) {
      setError("Cette adresse est incomplète (numéro civique, rue ou ville manquant).");
      return;
    }
    const parts = {
      address: `${result.houseNumber} ${result.road}`,
      town: result.town,
      province: result.province,
      postalCode: result.postalCode,
    };
    setAddress(parts.address);
    setTown(parts.town);
    setProvince(parts.province);
    setPostalCode(parts.postalCode);
    setVerifiedAddress(formatFullAddress(parts));
    setPositionAndFly(result.lat, result.lng, 17);
    setGeoResults([]);
    setError("");
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!ALLOWED_TYPES.includes(file.type)) {
      setError("Format d'image non supporté. Utilisez JPEG, PNG ou WebP.");
      return;
    }
    if (file.size > MAX_SIZE) {
      setError("Image trop volumineuse (max 5 Mo).");
      return;
    }
    setError("");
    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    objectUrlRef.current = URL.createObjectURL(file);
    setImageFile(file);
    setImagePreview(objectUrlRef.current);
    setRemoveImage(false);
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
    if (point?.image) setRemoveImage(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!name.trim()) return setError("Le nom est requis.");
    if (type === "weekly" && !locationId)
      return setError("Sélectionnez le lieu actif associé au tournoi hebdomadaire.");
    if (!allowedTypes.includes(type))
      return setError("Vous ne pouvez pas créer ce type de lieu.");
    if (!verifiedAddress || !position)
      return setError(
        "Localisez l'adresse complète (bouton « Localiser ») avant d'enregistrer."
      );

    setSaving(true);
    try {
      const payload = {
        type,
        name: name.trim(),
        address: address.trim(),
        town: town.trim(),
        province: province.trim(),
        postalCode: postalCode.trim(),
        fullAddress: verifiedAddress,
        description: description.trim(),
        website: website.trim(),
        lat: position.lat,
        lng: position.lng,
        locationId: type === "weekly" ? locationId : null,
      };

      let id = point?.id;
      if (isEdit) {
        await updateMapPoint(id, payload);
      } else {
        id = await createMapPoint({ ...payload, createdBy: currentUserId || null });
      }
      if (removeImage && point?.image) await deleteMapPointImage(id);
      if (imageFile) await uploadMapPointImage(id, imageFile);

      onSaved();
    } catch (err) {
      Sentry.captureException(err);
      console.error("Error saving map point:", err);
      setError("Une erreur est survenue lors de la sauvegarde.");
    } finally {
      setSaving(false);
    }
  };

  const draftPoints = position
    ? [{ id: "draft", type, name: name || "Nouveau lieu", lat: position.lat, lng: position.lng }]
    : [];

  return createPortal(
    <div
      className="fixed inset-0 z-[100] bg-black/70 flex items-start justify-center p-2 sm:p-6 overflow-y-auto"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-4xl bg-slate-900 border border-slate-700 rounded-lg shadow-2xl text-slate-50 my-auto"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800">
          <h2 className="text-xl font-bold">
            {isEdit ? "Modifier le lieu" : "Ajouter un lieu sur la carte"}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded hover:bg-slate-800 text-slate-400 hover:text-white transition"
            title="Fermer"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {error && (
            <p className="text-red-400 font-medium bg-red-950/40 border border-red-900 rounded px-3 py-2">
              {error}
            </p>
          )}

          {/* Type selector (hidden when only one type is allowed) */}
          {allowedTypes.length > 1 && (
          <div>
            <span className={labelClass}>Type de lieu</span>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
              {POINT_TYPE_LIST.filter((t) => allowedTypes.includes(t.key)).map((t) => {
                const Icon = t.Icon;
                const active = type === t.key;
                return (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setType(t.key)}
                    className={`flex items-center gap-3 p-3 rounded-lg border text-left transition ${
                      active
                        ? "border-amber-400 bg-slate-800 ring-2 ring-amber-400/40"
                        : "border-slate-700 bg-slate-800/50 hover:bg-slate-800"
                    }`}
                  >
                    <span
                      className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center"
                      style={{ background: t.color }}
                    >
                      <Icon size={20} color="#fff" strokeWidth={2.5} />
                    </span>
                    <span>
                      <span className="block font-semibold text-sm">{t.label}</span>
                      <span className="block text-xs text-slate-400 leading-snug">
                        {t.description}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Left column: details */}
            <div className="space-y-4">
              {type === "weekly" && (
                <div>
                  <label className={labelClass}>Lieu actif de la FQST *</label>
                  <select
                    value={locationId}
                    onChange={(e) => handleLocationChange(e.target.value)}
                    className={inputClass}
                    required
                  >
                    <option value="">— Sélectionner un lieu —</option>
                    {activeLocations.map((loc) => (
                      <option key={loc.id} value={loc.id}>
                        {loc.name}
                        {loc.table ? ` — ${loc.table}` : ""}
                        {loc.town ? ` (${loc.town})` : ""}
                      </option>
                    ))}
                  </select>
                  {activeLocations.length === 0 && (
                    <p className="text-xs text-amber-400 mt-1">Aucun lieu actif trouvé.</p>
                  )}
                </div>
              )}

              <div>
                <label className={labelClass}>Nom *</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className={inputClass}
                  required
                />
              </div>

              <div>
                <label className={labelClass}>Adresse (numéro civique et rue) *</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={address}
                    onChange={editAddressPart(setAddress)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleGeocode();
                      }
                    }}
                    placeholder="123 rue Principale"
                    className={inputClass}
                  />
                  <button
                    type="button"
                    onClick={handleGeocode}
                    disabled={geoLoading}
                    className="flex-shrink-0 inline-flex items-center gap-1 px-3 py-2 rounded bg-sky-700 hover:bg-sky-600 disabled:opacity-50 text-white text-sm font-semibold transition"
                    title="Trouver l'adresse sur la carte"
                  >
                    <Search size={16} />
                    {geoLoading ? "…" : "Localiser"}
                  </button>
                </div>
                {geoResults.length > 0 && (
                  <ul className="mt-2 rounded border border-slate-700 bg-slate-800 divide-y divide-slate-700 max-h-40 overflow-y-auto">
                    {geoResults.map((r, i) => (
                      <li key={`${r.lat}-${r.lng}-${i}`}>
                        <button
                          type="button"
                          onClick={() => applyGeoResult(r)}
                          disabled={!r.complete}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-slate-700 transition disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                        >
                          {r.label}
                          {!r.complete && (
                            <span className="block text-xs text-amber-400">
                              Adresse incomplète, ne peut pas être choisie
                            </span>
                          )}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div>
                <label className={labelClass}>Ville *</label>
                <input
                  type="text"
                  value={town}
                  onChange={editAddressPart(setTown)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleGeocode();
                    }
                  }}
                  className={inputClass}
                />
              </div>

              {verifiedAddress ? (
                <p className="flex items-start gap-2 text-sm text-emerald-300 bg-emerald-950/40 border border-emerald-900 rounded px-3 py-2">
                  <CircleCheck size={16} className="flex-shrink-0 mt-0.5" />
                  <span>
                    Adresse vérifiée : <strong>{verifiedAddress}</strong>
                  </span>
                </p>
              ) : (
                <p className="flex items-start gap-2 text-sm text-amber-300 bg-amber-950/30 border border-amber-900 rounded px-3 py-2">
                  <TriangleAlert size={16} className="flex-shrink-0 mt-0.5" />
                  <span>Adresse non vérifiée. Cliquez sur « Localiser » pour la confirmer.</span>
                </p>
              )}

              <div>
                <label className={labelClass}>Site web ou page Facebook</label>
                <input
                  type="url"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  placeholder="https://"
                  className={inputClass}
                />
              </div>

              <div>
                <label className={labelClass}>Description</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  placeholder="Heures d'ouverture, nombre de tables, soir de tournoi…"
                  className={inputClass}
                />
              </div>
            </div>

            {/* Right column: image + position */}
            <div className="space-y-4">
              <div>
                <span className={labelClass}>
                  Image{" "}
                  {type === "weekly" && (
                    <span className="text-slate-400 font-normal">
                      (l'image du lieu lié est utilisée en priorité)
                    </span>
                  )}
                </span>
                {linkedImage ? (
                  <div>
                    <img
                      src={linkedImage}
                      alt=""
                      className="w-full h-36 object-cover rounded-lg"
                    />
                    <p className="text-xs text-slate-400 mt-1">
                      Image du lieu « {linkedLocation.name} », gérée depuis la page du lieu.
                    </p>
                  </div>
                ) : imagePreview ? (
                  <div className="relative group">
                    <img
                      src={imagePreview}
                      alt=""
                      className="w-full h-36 object-cover rounded-lg"
                    />
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-1 rounded bg-black/60 hover:bg-red-600 text-white text-xs transition"
                      title="Retirer l'image"
                    >
                      <Trash2 size={14} /> Retirer
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full h-28 rounded-lg border-2 border-dashed border-slate-600 hover:border-slate-400 flex flex-col items-center justify-center text-slate-400 hover:text-slate-300 transition"
                  >
                    <ImagePlus size={28} className="mb-1" />
                    <span className="text-sm">
                      {type === "weekly" ? "Image de secours (optionnel)" : "Ajouter une image"}
                    </span>
                  </button>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={handleFileSelect}
                />
              </div>

              <div>
                <span className={labelClass}>
                  Position sur la carte *{" "}
                  <span className="text-slate-400 font-normal">
                    (glissez le repère pour ajuster sa position)
                  </span>
                </span>
                <InteractiveMap
                  points={draftPoints}
                  fitToPoints={false}
                  flyTo={flyTo}
                  onMarkerDrag={(_id, ll) => setPosition({ lat: ll.lat, lng: ll.lng })}
                  showPopups={false}
                  className="h-64 border border-slate-700"
                />
                <p className="text-xs text-slate-400 mt-1">
                  {position
                    ? `Repère placé : ${position.lat.toFixed(5)}, ${position.lng.toFixed(5)}`
                    : "Le repère apparaîtra après la localisation de l'adresse."}
                  {" · "}
                  <span style={{ color: getPointType(type).color }}>{getPointType(type).label}</span>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex flex-wrap justify-end gap-3 px-5 py-4 border-t border-slate-800">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="bg-slate-700 hover:bg-slate-600 text-white px-5 py-2 rounded-md font-semibold transition disabled:opacity-50"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={saving}
            className="bg-amber-500 hover:bg-amber-600 text-white px-5 py-2 rounded-md font-semibold transition disabled:opacity-50"
          >
            {saving ? "Enregistrement…" : isEdit ? "Mettre à jour" : "Ajouter"}
          </button>
        </div>
      </form>
    </div>,
    document.body
  );
};

export default MapPointForm;
